import { MenuItem, TextField } from '@mui/material';
import { useMemo, useState } from 'react';
import { Control, FieldValues, Path, UseFormWatch } from 'react-hook-form';
import { Customer, Partner } from '../../../@types/api';
import { SelectOption } from '../../../@types';
import { QUERY_KEYS } from '../../../constants';
import {
  canFilterCasinoReportsByAgent,
  canFilterCasinoReportsByManager,
  canFilterCasinoReportsByPlayer,
  CasinoReportRole,
  getCasinoReportRoleOptions,
  getDefaultCasinoReportRole,
} from '../../../helpers/casino';
import usePermissions from '../../../hooks/usePermissions';
import { getCompleteName } from '../../../utils';
import FormSelect from '../../molecules/FormSelect';
import FormAutocomplete from '../FormAutocomplete';

type CasinoRoleFilterFields = {
  role?: string;
  managerIds?: string[];
  agentIds?: string[];
  playerIds?: string[];
};

type Props<T extends FieldValues & CasinoRoleFilterFields> = {
  control: Control<T>;
  watch: UseFormWatch<T>;
  roleName?: Path<T>;
};

const ROLE_OPTIONS_BY_ID: SelectOption[] = [
  { id: 'manager', name: 'Manager' },
  { id: 'agent', name: 'Agent' },
  { id: 'player', name: 'Player' },
];

const MANAGER_IDS_FIELD = 'managerIds';
const AGENT_IDS_FIELD = 'agentIds';
const PLAYER_IDS_FIELD = 'playerIds';

const CasinoRoleFilters = <T extends FieldValues & CasinoRoleFilterFields>({ control, watch, roleName }: Props<T>) => {
  const { role: userRole } = usePermissions();
  const [localRole, setLocalRole] = useState<CasinoReportRole>(() => getDefaultCasinoReportRole(userRole));

  // Form values store casino userIds. Dependent CRM searches need CRM entity ids, so keep those ids locally.
  const [selectedManagerCrmIds, setSelectedManagerCrmIds] = useState<string[]>([]);
  const [selectedAgentCrmIds, setSelectedAgentCrmIds] = useState<string[]>([]);

  const roleOptions = useMemo(() => {
    const allowedRoles = getCasinoReportRoleOptions(userRole);

    return ROLE_OPTIONS_BY_ID.filter((option) => allowedRoles.includes(option.id as CasinoReportRole));
  }, [userRole]);

  const selectedRole = (roleName ? watch(roleName) : localRole) || getDefaultCasinoReportRole(userRole);
  const managerIds = watch(MANAGER_IDS_FIELD as Path<T>);
  const showManagerFilter = canFilterCasinoReportsByManager(userRole, selectedRole);
  const showAgentFilter = canFilterCasinoReportsByAgent(userRole, selectedRole);
  const showPlayerFilter = canFilterCasinoReportsByPlayer(selectedRole);
  const agentQueryParams = selectedManagerCrmIds.length
    ? {
        roleTypes: ['Agent'],
        filters: {
          teamLeadId: {
            values: selectedManagerCrmIds,
          },
        },
      }
    : { roleTypes: ['Agent'] };
  const playerSourceCrmIds = selectedAgentCrmIds.length ? selectedAgentCrmIds : selectedManagerCrmIds;
  const playerSourceRole = selectedAgentCrmIds.length ? 'Agent' : selectedManagerCrmIds.length ? 'Manager' : undefined;
  const playerSearchUrl = playerSourceCrmIds.length ? 'customer/by-agent-ids' : 'customer/search';
  const playerQueryParams = playerSourceCrmIds.length
    ? {
        role: playerSourceRole,
        agentIds: playerSourceCrmIds,
      }
    : undefined;

  return (
    <>
      {roleName ? (
        <FormSelect control={control} name={roleName} label='Role' options={roleOptions} />
      ) : (
        <TextField
          select
          label='Role'
          value={localRole}
          onChange={(event) => setLocalRole(event.target.value as CasinoReportRole)}
        >
          {roleOptions.map((option) => (
            <MenuItem key={option.id} value={option.id}>
              {option.name}
            </MenuItem>
          ))}
        </TextField>
      )}
      {showManagerFilter && (
        <FormAutocomplete<Partner, T>
          name={MANAGER_IDS_FIELD as Path<T>}
          control={control}
          queryKey={[QUERY_KEYS.agentSearch, 'casino-filter-managers']}
          apiService='crm'
          url='agent/search'
          label='Managers'
          multiple
          optionsFilter='userId'
          queryParams={{ roleTypes: ['Manager'] }}
          getOptionLabel={(options, value) => options.find((option) => option.userId === value)?.fullName ?? ''}
          onSelectionChange={(selectedManagers) => {
            setSelectedManagerCrmIds(selectedManagers.map((manager) => manager.id));
          }}
        />
      )}
      {showAgentFilter && (
        <FormAutocomplete<Partner, T>
          name={AGENT_IDS_FIELD as Path<T>}
          control={control}
          queryKey={[QUERY_KEYS.agentSearch, 'casino-filter-agents', managerIds, selectedManagerCrmIds]}
          apiService='crm'
          url='agent/search'
          label='Agents'
          multiple
          optionsFilter='userId'
          queryParams={agentQueryParams}
          getOptionLabel={(options, value) => options.find((option) => option.userId === value)?.fullName ?? ''}
          onSelectionChange={(selectedAgents) => {
            setSelectedAgentCrmIds(selectedAgents.map((agent) => agent.id));
          }}
        />
      )}
      {showPlayerFilter && (
        <FormAutocomplete<Customer, T>
          name={PLAYER_IDS_FIELD as Path<T>}
          control={control}
          queryKey={[QUERY_KEYS.customers, 'casino-filter-players', playerSearchUrl, playerSourceCrmIds]}
          apiService='crm'
          url={playerSearchUrl}
          label='Players'
          multiple
          queryParams={playerQueryParams}
          optionsFilter='userId'
          getOptionLabel={(options, value) => {
            const option = options.find((item) => item.userId === value);
            return option ? getCompleteName(option.firstName, option.lastName, option.username) : '';
          }}
        />
      )}
    </>
  );
};

export default CasinoRoleFilters;
